/**
 * 邮编电子围栏表格列（详情页与围栏 Tab 共用）
 *
 * 通过 buildGeofenceColumns(options) 生成列定义，options 控制：
 * - onIdClick：点击 ID 的行为（详情页=地图定位，围栏 Tab=跳转详情页）
 * - onAudit / onDelete：审核 / 删除回调
 * - showZipcodeColumn：是否展示「邮编」列（围栏 Tab 为全列表，需要展示）
 */
import React from 'react';
import { Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TFunction } from 'i18next';
import { ZipcodeMapGeofenceType } from '../graphql/queries/getZipcodeGeofenceListing';
import { getUserInfo } from '../../../../utils/auth';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';
import { formatTimestampToDateTime } from '../../../../utils/dateTimeUtils';

// 描述类型 显示文案映射：value -> key
export const descriptionTypeLabelMap: Record<string, string> = {
    PO_BOX_OR_POST_OFFICE: 'USPS',
    MILITARY: 'MILITARY',
    BLACK_FENCE_LIST: 'OTHER',
};

export const getDescriptionLabel = (descriptionType?: string | null, descriptionOther?: string | null) => {
    if (!descriptionType) return '-';
    if (descriptionType === 'BLACK_FENCE_LIST' && descriptionOther) {
        return descriptionOther;
    }
    return descriptionTypeLabelMap[descriptionType] || descriptionType;
};

/** 围栏状态标签配置（列表与详情弹窗共用）：标签样式 + 国际化文案 */
export const getGeofenceStatusConfig = (status: string, t: TFunction): { color: string; text: string } => {
    const statusConfig: Record<string, { color: string; text: string }> = {
        CREATED: { color: 'bg-yellow-100 text-yellow-700', text: t('wms.zipcodeMapGeofence.status.created') },
        APPROVED: { color: 'bg-blue-100 text-blue-700', text: t('wms.zipcodeMapGeofence.status.approved') },
        EFFECTIVE: { color: 'bg-green-100 text-green-700', text: t('wms.zipcodeMapGeofence.status.effective') },
        INACTIVE: { color: 'bg-red-100 text-red-700', text: t('wms.zipcodeMapGeofence.status.inactive') },
    };
    return statusConfig[status] || { color: 'bg-gray-100 text-gray-700', text: status };
};

export interface BuildGeofenceColumnsOptions {
    t: TFunction;
    onIdClick: (record: ZipcodeMapGeofenceType) => void;
    onAudit: (record: ZipcodeMapGeofenceType) => void;
    /** 删除回调；不传则不展示删除按钮 */
    onDelete?: (record: ZipcodeMapGeofenceType) => void;
    /** 是否展示「邮编」列（围栏 Tab 列表需要） */
    showZipcodeColumn?: boolean;
    /** 查看回调（围栏 Tab 列表需要：根据行 zipcode 跳转地图）；不传则不展示查看按钮 */
    onView?: (record: ZipcodeMapGeofenceType) => void;
    /** 审核按钮可点击的状态（默认 CREATED=hub 审核；终审页传 APPROVED=待终审） */
    auditableStatus?: string;
    /**
     * 仅当状态等于 auditableStatus 时才显示审核按钮；其他状态该行操作列显示 "-"
     * （默认 false：审核按钮始终显示，不满足状态时置灰）
     */
    auditOnlyWhenAuditable?: boolean;
    /** 审核按钮权限码（任一命中即显示；默认 WMS 审核双码，终审页传 [GEOFENCE_BLACKLIST_REVIEW]） */
    auditResourceCodes?: string[];
}

export const buildGeofenceColumns = ({
    t,
    onIdClick,
    onAudit,
    onDelete,
    showZipcodeColumn = false,
    onView,
    auditableStatus = 'CREATED',
    auditOnlyWhenAuditable = false,
    auditResourceCodes = [ResourceCode.zipcodeGeofence.HUB_AUDIT, ResourceCode.zipcodeGeofence.WHS_AUDIT],
}: BuildGeofenceColumnsOptions): ColumnsType<ZipcodeMapGeofenceType> => {
    const columns: ColumnsType<ZipcodeMapGeofenceType> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 50,
            render: (id: number, record: ZipcodeMapGeofenceType) => (
                <span
                    className="text-blue-600 cursor-pointer hover:text-blue-800 hover:underline"
                    onClick={() => onIdClick(record)}
                >
                    {id}
                </span>
            ),
        },
    ];

    if (showZipcodeColumn) {
        columns.push({
            title: t('central.zipcodes.filters.zipcode'),
            dataIndex: 'zipcode',
            key: 'zipcode',
            width: 90,
            render: (text) => text || '-',
        });
    }

    columns.push(
        {
            title: t('wms.zipcodeMapGeofence.columns.hubName'),
            dataIndex: 'hubName',
            key: 'hubName',
            width: 120,
            render: (text) => text || '-',
        },
        {
            title: t('wms.zipcodeMapGeofence.columns.warehouseName'),
            dataIndex: 'warehouseName',
            key: 'warehouseName',
            width: 120,
            render: (text) => text || '-',
        },
        {
            title: t('wms.zipcodeMapGeofence.columns.geofenceName'),
            dataIndex: 'geofenceName',
            key: 'geofenceName',
            width: 100,
            ellipsis: true,
        },
        {
            title: t('wms.zipcodeMapGeofence.columns.description'),
            dataIndex: 'descriptionType',
            key: 'descriptionType',
            width: 100,
            render: (descriptionType: string, record: ZipcodeMapGeofenceType) => {
                return getDescriptionLabel(descriptionType, record.descriptionOther);
            },
        },
        {
            title: t('wms.zipcodeMapGeofence.columns.deliveredTotal'),
            dataIndex: 'deliveredTotal',
            key: 'deliveredTotal',
            width: 120,
            render: (value: number) => value ?? 0,
        },
        {
            title: t('wms.zipcodeMapGeofence.columns.creatorName'),
            dataIndex: 'creatorName',
            key: 'creatorName',
            width: 120,
            render: (text) => text || '-',
        },
        {
            title: t('wms.zipcodeMapGeofence.columns.createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (createdAt: string) => (
                <span className="text-sm">{createdAt ? formatTimestampToDateTime(createdAt) : '--'}</span>
            ),
        },
        {
            title: t('wms.zipcodeMapGeofence.columns.status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            ellipsis: true,
            render: (status: string) => {
                const config = getGeofenceStatusConfig(status, t);
                return (
                    <span className={`px-2 py-1 rounded text-xs font-medium ${config.color}`}>
                        {config.text}
                    </span>
                );
            },
        },
        {
            title: t('wms.zipcodeMapGeofence.columns.actions'),
            key: 'action',
            width: 120,
            fixed: 'right',
            render: (_, record) => {
                const auditable = record.status === auditableStatus;
                const showView = !!onView;
                // auditOnlyWhenAuditable 模式：仅可审核状态显示审核按钮；否则常显但置灰
                const showAudit = hasResource(auditResourceCodes) && (auditOnlyWhenAuditable ? auditable : true);
                // 持页面全量权限码可删全部；否则（如只持 WHS 码）仅本人创建的围栏可删
                const currentUserId = getUserInfo()?.account.id;
                const canDeleteAll = hasResource([
                    ResourceCode.menu.HUB_GEOFENCE_BLACKLIST,
                    ResourceCode.menu.GEOFENCE_BLACKLIST_REVIEW,
                ]);
                const isCreator = currentUserId != null && record.creatorId?.toString() === currentUserId;
                const showDelete = !!onDelete && (canDeleteAll || isCreator);

                // 该模式下若无任何可用操作，整列显示 "-"
                if (auditOnlyWhenAuditable && !showView && !showAudit && !showDelete) {
                    return <span className="text-gray-400">-</span>;
                }

                return (
                    <div className="flex gap-2">
                        {showView && (
                        <Button
                            type="link"
                            size="small"
                            onClick={() => onView!(record)}
                        >
                            {t('wms.zipcodeMapGeofence.actions.view')}
                        </Button>
                        )}
                        {showAudit && (
                        <Button
                            type="link"
                            size="small"
                            disabled={!auditOnlyWhenAuditable && !auditable}
                            onClick={() => onAudit(record)}
                        >
                            {t('wms.zipcodeMapGeofence.actions.audit')}
                        </Button>
                        )}
                        {showDelete && (
                        <Button
                            type="link"
                            size="small"
                            danger
                            onClick={() => onDelete!(record)}
                        >
                            {t('wms.zipcodeMapGeofence.actions.delete')}
                        </Button>
                        )}
                    </div>
                );
            },
        },
    );

    return columns;
};
